import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@commons/header";
import Row from "@components/row";
import Table from "@components/table";
import SubscriptionCard from "@commons/cards/subscription";
import TransactionCard from "@commons/cards/transaction";
import { useSelector, useDispatch } from "react-redux";
import { Actions, RootState } from "@store/index";
import { Module, Store, Chat } from "@apis/index";
import theme from "@utils/theme";
import { CheckCircle, XCircle, MessageSquare, Wallet } from "lucide-react";
import { deletesvg, edit, eye, switchOff, switchOn } from "@assets/icons";
import Tab from "@components/tabs";
import { useModal } from "@context/ModalContext";
import WalletAdjustmentModal from "@components/modals/WalletAdjustmentModal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import Svg from "@components/svg";
import DeleteModal from "@components/delete-modal";
import useConfig from "@hooks/useConfig";
import { toast } from "react-toastify";
import usePermission from "@hooks/usePermission";
import { useQueryState } from "@hooks/useQueryState";

const StoreListTable = ({
  type,
  moduleId,
}: {
  type?: "pending" | "rejected" | "verified";
  moduleId?: number;
}) => {
  const { showModal, hideModal } = useModal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");
  const { hasPermission } = usePermission();
  const { settings: currencySettings } = useGeneralSettings();

  const {
    items: stores,
    totalPages,
    totalItems,
    loading,
  } = useSelector((state: RootState) => state.stores);

  const { data: modules } = useSelector((state: RootState) => state.modules);

  const status =
    type === "pending"
      ? "Pending"
      : type === "rejected"
        ? "Rejected"
        : "Verified";

  const fetchStores = useCallback(async () => {
    await Store.list({
      state: "stores",
      params: { page, status, moduleId, search, limit: 30 },
    });
  }, [moduleId, page, status, search]);

  const fetchModules = useCallback(async () => {
    await Module.list({ state: "modules", params: { isActive: true } });
  }, []);
  
  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    setPage(1);
  }, []);

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
  }, [moduleId, search]);
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);
  useEffect(() => {
    fetchModules();
  }, []);
  const { bucketUrl } = useConfig();

  const handleStatusToggle = useCallback(
    async (item: any, field: string, newValue: any, useString = false) => {
      if (!hasPermission("store", "Edit")) {
        toast.error("You don't have permission to edit stores");
        return;
      }

      try {
        await toast.promise(
          Store.update({
            state: "stores",
            id: item.id,
            data: { [field]: useString ? String(newValue) : newValue },
          }),
          {
            pending: "Updating store status...",
            success: "Store status updated successfully!",
            error: "Failed to update store status.",
          }
        );

        // Only update Redux on successful update
        dispatch(Actions.update("stores", { id: item.id, [field]: newValue }));
      } catch (err) {
        console.error("Failed to update store status:", err);
      }
    },
    [dispatch, hasPermission],
  );

  const columns = [
    {
      title: "Store Info",
      key: "storeInfo",
      render: (item: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "start",
          }}
        >
          {item.avatar ? (
            <img
              src={`${bucketUrl}/${item.avatar}`}
              alt={item.name}
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600,
                color: "#6b7280",
                textTransform: "uppercase",
              }}
            >
              {(item.storeName || item.name || "")
                .split(" ")
                .map((n: string) => n[0])
                .join("")
                .slice(0, 2)}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>
              {item.storeName || item.name}
            </span>
            <span style={{ color: "#666", fontSize: 14 }}>
              {item.storePhoneNo || item.phoneNo}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (item: any) => (
        <span style={{ color: "#666", fontSize: 14 }}>
          {item.storeEmail || item.email}
        </span>
      ),
    },
    {
      title: "Module",
      key: "module",
      render: (item: any) => item.module?.name || "-",
    },

    {
      title: "Joined Date",
      key: "joinedDate",
      render: (item: any) =>
        new Date(item.createdAt).toLocaleDateString() || "-",
    },
    {
      title: "is Pure Veg",
      key: "isPureVeg",
      render: (item) => (
        <Svg
          src={item.isPureVeg ? switchOn : switchOff}
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle(item, "isPureVeg", !item.isPureVeg);
          }}
        />
      ),
    },
    {
      title: "is Active",
      key: "isactive",
      render: (item) => (
        <Svg
          src={item.isActive ? switchOn : switchOff}
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle(item, "isActive", !item.isActive, true);
          }}
        />
      ),
    },
    {
      title: "is Featured",
      key: "isFeatured",
      render: (item) => (
        <Svg
          src={item.isFeatured ? switchOn : switchOff}
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle(item, "isFeatured", !item.isFeatured);
          }}
        />
      ),
    },
    {
      title: "is Popular",
      key: "isPopular",
      render: (item) => (
        <Svg
          src={item.isPopular ? switchOn : switchOff}
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle(item, "isPopular", !item.isPopular);
          }}
        />
      ),
    },
    {
      title: "Best Seller",
      key: "bestSeller",
      render: (item) => (
        <Svg
          src={item.bestSeller ? switchOn : switchOff}
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle(item, "bestSeller", !item.bestSeller);
          }}
        />
      ),
    },
  ];

  const confirmAction = useCallback(
    (title: string, onConfirm: () => void) => {
      showModal(
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h3
            style={{ fontSize: "18px", fontWeight: 600, marginBottom: "10px" }}
          >
            {title}
          </h3>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "12px" }}
          >
            <button
              onClick={() => {
                onConfirm();
                hideModal();
              }}
              style={{
                background: "#22c55e",
                color: "white",
                padding: "6px 16px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              Confirm
            </button>
            <button
              onClick={hideModal}
              style={{
                background: "#ef4444",
                color: "white",
                padding: "6px 16px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
      );
    },
    [showModal, hideModal],
  );

  const baseActions = [
    {
      key: "view",
      icon: eye,
      permission: "View List",
      onClick: (item: any) =>
        navigate(
          `/admin/users/store/store-lists/view/${item.id}?module=${item?.module?.name}`,
          { state: item },
        ),
    },

    {
      key: "edit",
      icon: edit,
      permission: "Edit",
      onClick: (item: any, e: any) => {
        e.stopPropagation();
        const stateToPass = { ...item };
        if (typeof item.geometry === "string") {
          try {
            stateToPass.geometry = JSON.parse(item.geometry);
          } catch (err) {
            console.error("Failed to parse geometry:", err);
            stateToPass.geometry = [];
          }
        }
        navigate(`/admin/users/store/update-store/${item.id}`, {
          state: stateToPass,
        });
      },
    },
    {
      key: "remove",
      icon: deletesvg,
      permission: "Delete",
      onClick: (item: any, e: any) => {
        e.stopPropagation();
        showModal(
          <DeleteModal
            state="stores"
            api={Store}
            id={item.id}
            onSuccess={() => fetchStores()}
          />,
        );
      },
    },
    {
      key: "wallet",
      icon: <Wallet size={16} color={theme.colors.green500} />,
      permission: "Edit",
      title: "Adjust Wallet",
      onClick: (item: any, e: any) => {
        e.stopPropagation();
        showModal(
          <WalletAdjustmentModal
            user={item}
            onClose={hideModal}
            onSuccess={fetchStores}
            currencySettings={currencySettings}
          />,
        );
      },
      style: {
        background: "#f0fdf4",
        border: "1px solid #bbf7d0",
        borderRadius: "8px",
        padding: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s",
      },
    },
  ].filter((a) => hasPermission("store", a.permission));

  const pendingActions =
    type === "pending"
      ? [
          {
            key: "view",
            icon: eye,
            permission: "View List",
            onClick: (item: any) =>
              navigate(
                `/admin/users/store/store-lists/view/${item.id}?module=${item.module?.name}`,
                { state: item },
              ),
          },
          {
            key: "accept",
            icon: <CheckCircle size={16} color="green" />,
            permission: "Edit",
            onClick: (item: any) => {
              confirmAction("Accept this store?", async () => {
                await Store.update({
                  state: "stores",
                  id: item.id,
                  data: { status: "Verified" },
                });
                fetchStores();
              });
            },
          },
          {
            key: "reject",
            icon: <XCircle size={16} color="red" />,
            permission: "Edit",
            onClick: (item: any) => {
              confirmAction("Reject this store?", async () => {
                await Store.update({
                  state: "stores",
                  id: item.id,
                  data: { status: "Rejected" },
                });
                fetchStores();
              });
            },
          },
        ].filter((a) => hasPermission("store", a.permission))
      : [];

  const actions =
    type === "pending"
      ? [...pendingActions, ...baseActions.filter((a) => a.key === "remove")]
      : baseActions;

  return (
    <>
      <br />
      <Table
        data={stores}
        columns={columns}
        actions={actions}
        totalItems={totalItems}
        totalPages={totalPages}
        itemsPerPage={30}
        pageControlled={page}
        onPageChange={setPage}
        clientSearch={true}
        searchKeys={["storeName", "storeEmail", "storePhoneNo", "name", "email", "phoneNo"]}
        onSearchChange={handleSearchChange}
        loading={loading}
        onRowClick={(item: any) =>
          navigate(
            `/admin/users/store/store-lists/view/${item.id}?module=${item.module?.name}`,
            { state: item },
          )
        }
      />
    </>
  );
};

export default StoreListTable;
